// WHAT: Main app component for TimeTracQ with tab navigation and state management
// WHY:  Provides central hub for all TimeTracQ functionality with comprehensive state management
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: Multiple hooks, tab components, modal components
// RISKS:
//   - NOTE: State management is complex - many useState hooks may cause performance issues
//   - NOTE: Dialog state management may have race conditions
//   - NOTE: Manual entry state is extensive - may be error-prone
//   - NOTE: Tab navigation may not sync with URL correctly

import { core } from "@tauri-apps/api";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Card } from "@/components/ui";
import { debugProductivity } from "@/domains/logging/utils/debugLog";

import { ManualEntryDialog } from "./components/ManualEntryDialog";
import { TaskEditModal } from "./components/molecules/TaskEditModal";
import { TrackingCompletionDialog, type TrackingCompletionData } from "./components/NotesDialog";
import { type OverviewEntry } from "./components/pages/OverviewPage";
import { ProximityModal } from "./components/ProximityModal";
import { useSupabaseTasks, type SupabaseTask } from "./hooks/data/useSupabasetasks";
import { useBuzzerControl } from "./hooks/time_tracking/useBuzzerControl";
import { useTimeTracqControl } from "./hooks/time_tracking/useTimeTracqControl";
import { useWorkspaceConfig } from "./hooks/time_tracking/useWorkspaceConfig";
import { useDailyPlan } from "./hooks/useDailyPlan";
import { useDataFetcher } from "./hooks/useDataFetcher";
import {
  TrackingTab,
  TasksKanbanBoard,
  DailyPlanTab,
  ShortfallsTab,
  TimeTracQOverviewTab as OverviewTab,
  AggregatedViewTab,
  BuzzerTab,
} from "./screen/tabs";

/**
 * Tab navigation configuration
 */
const tabs = [
  { id: "tracking", label: "Tracking", path: "/timetracq" },
  { id: "tasks", label: "Tasks", path: "/timetracq/tasks" },
  { id: "daily-plan", label: "Daily Plan", path: "/timetracq/daily-plan" },
  { id: "shortfalls", label: "Shortfalls", path: "/timetracq/shortfalls" },
  { id: "overview", label: "Overview", path: "/timetracq/overview" },
  { id: "analytics", label: "Analytics", path: "/timetracq/analytics" },
  { id: "buzzer", label: "Buzzer", path: "/timetracq/buzzer" },
];

/**
 * WHAT:
 *   Main app component that provides central hub for all TimeTracQ functionality
 *   with tab navigation, state management, and modal dialogs.
 *
 * WHY:
 *   Centralizes all TimeTracQ features in one component with comprehensive
 *   state management and navigation.
 *
 * BEHAVIOR:
 *   - Manages tab navigation
 *   - Handles tracking state and controls
 *   - Manages task CRUD operations
 *   - Handles manual entry dialog
 *   - Manages completion dialog
 *   - Handles proximity modal
 *   - Fetches and displays data from multiple hooks
 *   - Updates when data changes
 *
 * RISKS:
 *   - NOTE: State management is complex - many useState hooks may cause performance issues
 *   - NOTE: Dialog state management may have race conditions
 *   - NOTE: Manual entry state is extensive - may be error-prone
 *   - NOTE: Tab navigation may not sync with URL correctly
 *
 * @returns The TimeTracQ app component with tab navigation and modals
 *
 */
const TimeTracQApp = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(tabs[0]);
  const [taskFilter, setTaskFilter] = useState("");
  const [editingTask, setEditingTask] = useState<SupabaseTask | undefined>();
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [preselectedWorkspace, setPreselectedWorkspace] = useState<string | undefined>();

  // Manual entry modal state
  const [manualWorkspace, setManualWorkspace] = useState("");
  const [manualAspect, setManualAspect] = useState("");
  const [manualStart, setManualStart] = useState("");
  const [manualDuration, setManualDuration] = useState("");
  const [manualNotes, setManualNotes] = useState("");
  const [manualEfficiencyRating, setManualEfficiencyRating] = useState<null | number>(5);
  const [manualWhatWentWell, setManualWhatWentWell] = useState("");
  const [manualWhatWasInefficient, setManualWhatWasInefficient] = useState("");
  const [manualImpactScore, setManualImpactScore] = useState("");
  const [shortfallViewMode, setShortfallViewMode] = useState<"full" | "today">("today");
  const [overviewMode, setOverviewMode] = useState<"aspect" | "workspace">("workspace");
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showProximityModal, setShowProximityModal] = useState(false);

  const [config, , isLoading] = useWorkspaceConfig();
  const {
    isTracking,
    session,
    startTracking,
    stopTracking: stopTrackingInternal,
  } = useTimeTracqControl();

  /**
   * WHAT:
   *   Wrapper for stopTracking that opens completion dialog.
   *
   * WHY:
   *   Enables users to add completion data before stopping tracking.
   *
   * BEHAVIOR:
   *   - Validates tracking state
   *   - Opens completion dialog instead of stopping immediately
   */
  const stopTracking = useCallback(() => {
    debugProductivity("info", "timetracq", "stopTracking called", {
      has_session: !!session,
      is_tracking: isTracking,
      session_id: session?.id,
      show_completion_dialog: showCompletionDialog,
    });

    // NOTE: If dialog already open, ignore
    if (showCompletionDialog) {
      debugProductivity(
        "warn",
        "timetracq",
        "Stop tracking called but completion dialog already open",
        {
          is_tracking: isTracking,
          session_id: session?.id,
        },
      );
      return;
    }

    if (!session) {
      debugProductivity("warn", "timetracq", "Stop tracking called but no active session", {
        is_tracking: isTracking,
        show_completion_dialog: showCompletionDialog,
      });
      return;
    }

    if (!isTracking) {
      debugProductivity("warn", "timetracq", "Stop tracking called but isTracking is false", {
        session_id: session.id,
        show_completion_dialog: showCompletionDialog,
      });
      return;
    }

    debugProductivity("info", "timetracq", "Opening completion dialog", {
      aspect: session.aspect,
      session_id: session.id,
      workspace: session.workspace,
    });
    setShowCompletionDialog(true);
  }, [session, isTracking, showCompletionDialog]);

  // NOTE: Reset dialog state when tracking stops
  useEffect(() => {
    debugProductivity("info", "timetracq", "Tracking state changed", {
      has_session: !!session,
      is_tracking: isTracking,
      session_id: session?.id,
      show_completion_dialog: showCompletionDialog,
    });

    if (!isTracking && !session && showCompletionDialog) {
      debugProductivity(
        "info",
        "timetracq",
        "Resetting completion dialog - no tracking and no session",
        {},
      );
      setShowCompletionDialog(false);
    }
  }, [isTracking, session, showCompletionDialog]);

  /**
   * WHAT:
   *   Handles tracking completion with data from dialog.
   *
   * WHY:
   *   Saves completion data and stops tracking session.
   *
   * BEHAVIOR:
   *   - Updates session with completion data
   *   - Stops tracking
   *   - Closes dialog
   *
   * @param data
   *
   */
  const handleTrackingCompletion = async (data: TrackingCompletionData) => {
    if (!session) {
      debugProductivity(
        "warn",
        "timetracq",
        "Tracking completion called but no active session",
        {},
      );
      return;
    }

    debugProductivity("info", "timetracq", "Completing tracking session", {
      has_efficiency_rating: data.efficiencyRating !== undefined,
      has_notes: !!data.notes,
      has_what_was_inefficient: !!data.whatWasInefficient,
      has_what_went_well: !!data.whatWentWell,
      session_id: session.id,
    });

    try {
      await core.invoke("update_tracking_session", {
        efficiencyRating: data.efficiencyRating ?? null,
        endTime: new Date().toISOString(),
        id: session.id,
        impactScore: data.impactScore ?? null,
        notes: data.notes ?? null,
        taskId: data.taskId ?? null,
        whatWasInefficient: data.whatWasInefficient ?? null,
        whatWentWell: data.whatWentWell ?? null,
      });

      debugProductivity(
        "info",
        "timetracq",
        "Tracking session updated successfully, stopping tracking",
        {
          session_id: session.id,
        },
      );

      setShowCompletionDialog(false);

      // Wichtig: stopTrackingInternal aufrufen, um die Session zu stoppen
      // Dies setzt session auf null und aktualisiert den State
      // Hinweis: stopTrackingInternal ruft update_tracking_session nochmal auf,
      // aber das ist ok, da wir bereits alle Daten gesetzt haben
      // Das zweite Update setzt nur endTime und notes, was die anderen Felder nicht überschreibt
      stopTrackingInternal(data.notes ?? undefined);

      debugProductivity("info", "timetracq", "Tracking session stopped after completion", {
        session_id: session.id,
      });
    } catch (error) {
      debugProductivity("error", "timetracq", "Error completing tracking session", {
        error: String(error),
        session_id: session.id,
      });
      // Dialog bleibt offen bei Fehler
    }
  };

  const { createTask, deleteTask, tasks, updateTask } = useSupabaseTasks();

  const {
    aspectOverview,
    expectedTotal,
    isLoading: dataFetcherLoading,
    onTrackFactor,
    shortfalls,
    workedToday,
    workedTotal,
    workspaceOverview,
  } = useDataFetcher({
    config: config as Record<string, Record<string, { color: string; hours: number }>>,
    shortfallViewMode: "today",
    tasks,
  });

  const { dailyCapacity, dailyPlan } = useDailyPlan({
    config: config as Record<string, Record<string, { color: string; hours: number }>>,
    shortfalls,
    shortfallViewMode: "today",
    tasks,
    workedToday: workedToday,
  });

  // Handler für Proximity Detection
  const handleProximityDetected = useCallback(() => {
    if (isTracking && !showProximityModal) {
      debugProductivity("info", "timetracq", "Proximity detected during tracking, opening modal", {
        is_tracking: isTracking,
        session_id: session?.id,
      });
      setShowProximityModal(true);
    }
  }, [isTracking, showProximityModal, session?.id]);

  // Handler für Proximity Lost
  const handleProximityLost = useCallback(() => {
    if (showProximityModal) {
      debugProductivity("info", "timetracq", "Proximity lost, closing modal", {
        is_tracking: isTracking,
      });
      setShowProximityModal(false);
    }
  }, [showProximityModal, isTracking]);

  const {
    confirmedWorkspace,
    currentOptions,
    currentSelectionIndex,
    lastHeartbeat,
    mode,
    proximityState,
  } = useBuzzerControl({
    config,
    isCompletionDialogOpen: showCompletionDialog,
    isTracking,
    onProximityDetected: handleProximityDetected,
    onProximityLost: handleProximityLost,
    startTracking,
    stopTracking,
  });

  // FIX: Remove the problematic useEffect that reacts to heartbeat
  // The heartbeat should not trigger re-renders of the entire component
  // useEffect(() => {
  //   console.log('🔧 DEBUG: Heartbeat useEffect triggered, lastHeartbeat:', lastHeartbeat);
  //   // This useEffect will run every time lastHeartbeat changes (every second)
  //   // In the original component, this might have triggered other state changes
  // }, [lastHeartbeat]);

  const handleTabClick = (tab: (typeof tabs)[0]) => {
    setCurrentTab(tab);
    const result = navigate(tab.path);
    if (result instanceof Promise) {
      result.catch(() => {
        // Navigation errors are handled silently
      });
    }
  };

  const handleManualSave = async () => {
    // Enhanced validation with detailed error messages
    if (!manualWorkspace) {
      alert("Bitte einen Workspace auswählen");
      return;
    }
    if (!manualAspect) {
      alert("Bitte einen Aspect auswählen");
      return;
    }
    if (!manualStart) {
      alert("Bitte ein Datum und Uhrzeit eingeben");
      return;
    }
    if (!manualDuration) {
      alert("Bitte eine Dauer in Minuten eingeben");
      return;
    }

    try {
      // Parse duration from minutes to integer
      const durationMinutes = Number.parseInt(manualDuration, 10);
      if (Number.isNaN(durationMinutes) || durationMinutes <= 0) {
        alert("Bitte eine gültige Dauer in Minuten eingeben (z.B. 45)");
        return;
      }

      debugProductivity(
        "info",
        "timetracq",
        "Preparing manual entry (Supabase will generate UUID)",
        {
          aspect: manualAspect,
          duration: durationMinutes,
          efficiencyRating: manualEfficiencyRating,
          impactScore: manualImpactScore,
          notes: manualNotes,
          start: manualStart,
          whatWasInefficient: manualWhatWasInefficient,
          whatWentWell: manualWhatWentWell,
          workspace: manualWorkspace,
        },
      );

      // Convert datetime-local format to RFC3339 format for backend
      const startDateTime = new Date(manualStart);
      if (Number.isNaN(startDateTime.getTime())) {
        alert("Bitte ein gültiges Datum und Uhrzeit eingeben");
        return;
      }
      const startTimeRFC3339 = startDateTime.toISOString();

      debugProductivity("info", "timetracq", "Converted start time", {
        converted: startTimeRFC3339,
        dateObject: startDateTime,
        original: manualStart,
      });

      // Prepare the request payload (camelCase for Tauri, no ID - Supabase will generate it)
      const requestPayload = {
        aspect: manualAspect,
        durationMinutes: durationMinutes,
        efficiencyRating: manualEfficiencyRating,
        impactScore: manualImpactScore || null,
        notes: manualNotes || null,
        startTime: startTimeRFC3339,
        whatWasInefficient: manualWhatWasInefficient || null,
        whatWentWell: manualWhatWentWell || null,
        workspace: manualWorkspace,
      };

      debugProductivity("info", "timetracq", "Calling backend with payload", requestPayload);

      // Call backend API to save manual time entry
      const result = await core.invoke("insert_tracking_session", requestPayload);

      debugProductivity("info", "timetracq", "Manual entry saved successfully", { result });

      setShowManualModal(false);
      // Reset form
      setManualWorkspace("");
      setManualAspect("");
      setManualStart("");
      setManualDuration("");
      setManualNotes("");
      setManualEfficiencyRating(5);
      setManualWhatWentWell("");
      setManualWhatWasInefficient("");
      setManualImpactScore("");

      // Show success message
      alert("Zeiteintrag erfolgreich gespeichert!");
    } catch (error) {
      debugProductivity("error", "timetracq", "Error saving manual entry", {
        error: String(error),
      });
      alert("Fehler beim Speichern der Eingabe: " + String(error));
    }
  };

  // Show loading state
  if (isLoading || dataFetcherLoading) {
    return <div className="text-white p-6">🔄 Lade Workspace-Konfiguration...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* TracQ Header */}
        <Card
          className="border-tracq-deep-blue/30 bg-gradient-to-r from-slate-800/50 to-slate-700/30"
          padding="lg"
          variant="default"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-white">TimeTracQ</h1>
            </div>
            <p className="text-slate-400">Track your time and manage your productivity.</p>
          </div>
        </Card>

        {/* Tab Navigation */}
        <nav className="border-b border-slate-700 mb-6">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-200 ${
                  currentTab?.id === tab.id
                    ? "border-tracq-bright-blue text-tracq-bright-blue"
                    : "border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-600"
                }`}
                key={tab.id}
                onClick={() => {
                  handleTabClick(tab);
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Tab Content */}
        <div className="space-y-6">
          {currentTab?.id === "tracking" && (
            <Card
              className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
              padding="lg"
              variant="default"
            >
              <TrackingTab
                confirmedWorkspace={confirmedWorkspace ?? ""}
                currentOptions={currentOptions}
                currentSelectionIndex={currentSelectionIndex}
                dailyCapacity={dailyCapacity}
                expectedTotal={expectedTotal}
                handleStopTracking={() => {
                  debugProductivity("info", "timetracq", "Stop tracking clicked", {
                    has_session: !!session,
                    has_stopTracking: typeof stopTracking === "function",
                    is_tracking: isTracking,
                    show_completion_dialog: showCompletionDialog,
                  });
                  try {
                    stopTracking();
                  } catch (error) {
                    debugProductivity("error", "timetracq", "Error calling stopTracking", {
                      error: String(error),
                      error_message: error instanceof Error ? error.message : String(error),
                    });
                  }
                }}
                isTracking={isTracking}
                lastHeartbeat={lastHeartbeat}
                mode={mode}
                onTrackFactor={onTrackFactor ?? null}
                proximityState={proximityState}
                setShowManualModal={setShowManualModal}
                workedToday={workedToday}
                workedTotal={workedTotal}
              />
            </Card>
          )}

          {currentTab?.id === "tasks" && (
            <Card
              className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
              padding="lg"
              variant="default"
            >
              <TasksKanbanBoard
                deleteTask={deleteTask}
                setEditingTask={setEditingTask}
                setPreselectedWorkspace={setPreselectedWorkspace}
                setShowTaskModal={setShowTaskModal}
                setTaskFilter={setTaskFilter}
                taskFilter={taskFilter}
                tasks={tasks}
                updateTask={updateTask}
              />
            </Card>
          )}

          {currentTab?.id === "daily-plan" && (
            <Card
              className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
              padding="lg"
              variant="default"
            >
              <DailyPlanTab
                dailyPlan={dailyPlan}
                workspaceConfig={
                  config as Record<string, Record<string, { color: string; hours: number }>>
                }
              />
            </Card>
          )}

          {currentTab?.id === "shortfalls" && (
            <Card
              className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
              padding="lg"
              variant="default"
            >
              <ShortfallsTab
                setShortfallViewMode={setShortfallViewMode}
                shortfalls={shortfalls}
                shortfallViewMode={shortfallViewMode}
              />
            </Card>
          )}

          {currentTab?.id === "overview" && (
            <Card
              className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
              padding="lg"
              variant="default"
            >
              <OverviewTab
                aspectOverview={aspectOverview as unknown as OverviewEntry[]}
                overviewMode={overviewMode}
                setOverviewMode={setOverviewMode}
                workspaceOverview={workspaceOverview as unknown as OverviewEntry[]}
              />
            </Card>
          )}

          {currentTab?.id === "analytics" && (
            <Card
              className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
              padding="lg"
              variant="default"
            >
              <AggregatedViewTab />
            </Card>
          )}

          {currentTab?.id === "buzzer" && (
            <Card
              className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
              padding="lg"
              variant="default"
            >
              <BuzzerTab />
            </Card>
          )}
        </div>
      </div>

      {/* Task Edit Modal */}
      <TaskEditModal
        onClose={() => {
          debugProductivity("debug", "timetracq", "TaskEditModal onClose");
          setShowTaskModal(false);
          setEditingTask(undefined);
          setPreselectedWorkspace(undefined);
        }}
        onDelete={async (id) => {
          debugProductivity("debug", "timetracq", "TaskEditModal onDelete", {
            id,
          });
          await deleteTask(id);
          setShowTaskModal(false);
          setEditingTask(undefined);
        }}
        onSave={async (taskForm) => {
          debugProductivity("debug", "timetracq", "TaskEditModal onSave", {
            taskForm,
          });
          try {
            if (editingTask) {
              // Update existing task
              await updateTask(editingTask.id, taskForm);
              debugProductivity("info", "timetracq", "Task updated successfully");
            } else {
              // Create new task
              await createTask(taskForm);
              debugProductivity("info", "timetracq", "Task created successfully");
            }
            setShowTaskModal(false);
            setEditingTask(undefined);
            setPreselectedWorkspace(undefined);
          } catch (error) {
            debugProductivity("error", "timetracq", "Error saving task", {
              error: String(error),
            });
          }
        }}
        open={showTaskModal}
        {...(preselectedWorkspace === undefined ? {} : { preselectedWorkspace })}
        {...(editingTask === undefined ? {} : { task: editingTask })}
      />

      {/* Manual Entry Modal */}
      <ManualEntryDialog
        aspectOptions={manualWorkspace ? Object.keys(config[manualWorkspace] ?? {}) : []}
        duration={manualDuration}
        efficiencyRating={manualEfficiencyRating}
        impactScore={manualImpactScore}
        notes={manualNotes}
        onAspectChange={setManualAspect}
        onClose={() => {
          setShowManualModal(false);
          // Reset form on close
          setManualWorkspace("");
          setManualAspect("");
          setManualStart("");
          setManualDuration("");
          setManualNotes("");
          setManualEfficiencyRating(5);
          setManualWhatWentWell("");
          setManualWhatWasInefficient("");
          setManualImpactScore("");
        }}
        onDurationChange={setManualDuration}
        onEfficiencyRatingChange={setManualEfficiencyRating}
        onImpactScoreChange={setManualImpactScore}
        onNotesChange={setManualNotes}
        onSave={() => {
          void handleManualSave();
        }}
        onStartChange={setManualStart}
        onWhatWasInefficientChange={setManualWhatWasInefficient}
        onWhatWentWellChange={setManualWhatWentWell}
        onWorkspaceChange={setManualWorkspace}
        open={showManualModal}
        selectedAspect={manualAspect}
        selectedWorkspace={manualWorkspace}
        start={manualStart}
        whatWasInefficient={manualWhatWasInefficient}
        whatWentWell={manualWhatWentWell}
        workspaceOptions={Object.keys(config).filter(
          (k) => !["Settings", "Meta", "System", "Version"].includes(k),
        )}
      />

      {/* Tracking Completion Dialog */}
      {isTracking && session && showCompletionDialog ? (
        <TrackingCompletionDialog
          onClose={() => {
            debugProductivity("info", "timetracq", "Completion dialog closed", {
              session_id: session.id,
            });
            setShowCompletionDialog(false);
          }}
          onConfirm={(data) => {
            void handleTrackingCompletion(data);
          }}
          open={isTracking}
        />
      ) : null}

      {/* Proximity Modal */}
      {isTracking && showProximityModal && session ? (
        <ProximityModal
          onClose={() => {
            debugProductivity("info", "timetracq", "Proximity modal closed", {
              is_tracking: isTracking,
            });
            setShowProximityModal(false);
          }}
          open={showProximityModal}
          session={session}
        />
      ) : null}
    </div>
  );
};

export { TimeTracQApp };
